import { ApolloClient, ApolloError, NormalizedCacheObject } from '@apollo/client';

import { isSubscriptionError } from '@/shared/api';
import { PullCollectionDocument, PushCollectionDocument } from '@/shared/generated/graphql';
import { CollectionName, SerializedCollection } from '@/shared/lib/sj-orm';

import { Loader } from './types';

export class GraphQLLoader implements Loader {
  constructor(
    private readonly client: ApolloClient<NormalizedCacheObject>,
    private readonly onSubscriptionError: () => void,
  ) {}

  private onRequestError(error: unknown) {
    const apolloError = error as ApolloError;
    const code = apolloError.graphQLErrors[0].extensions.code as string;

    if (isSubscriptionError(code)) {
      this.onSubscriptionError();
      return;
    }

    throw error;
  }

  async push(collectionName: CollectionName, collection: SerializedCollection): Promise<boolean> {
    try {
      const mutationResult = await this.client.mutate({
        mutation: PushCollectionDocument,
        variables: {
          name: collectionName,
          data: collection,
        },
        fetchPolicy: 'network-only',
      });

      return mutationResult.data.pushCollection;
    } catch (error) {
      this.onRequestError(error);
      return false;
    }
  }

  async pull(collectionName: CollectionName): Promise<SerializedCollection | null> {
    try {
      const queryResult = await this.client.query({
        query: PullCollectionDocument,
        variables: {
          name: collectionName,
        },
        fetchPolicy: 'network-only',
      });

      return queryResult.data.collection1?.data ?? null;
    } catch (error) {
      this.onRequestError(error);
      return null;
    }
  }
}
